import { GameDocumentContext } from '../../../contexts/game-document';
import { useContext, useEffect, useState } from 'react';
import Toolbar from '../toolbar';
import {
  AddResourceAsync,
  GetResourceValue,
  GetTaskById,
  GetZoneById,
  SaveEntityEditorAsync,
  UpdateGameDocState
} from '../../../utils/game-document';
import { Button } from '@progress/kendo-react-buttons';
import {
  Link,
  matchPath,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import { EntityEditor } from '../../../types/game-document/entity-editor';
import {
  ResourceEntity,
  TaskContentEntity,
  TaskEntity
} from '../../../types/game-document/';
import { FluidForm } from '../../../components/forms';
import {
  AddTaskContentAsync,
  DeleteTaskContentAsync,
  GetTaskContentById,
  UpdateTaskContentAsync
} from '../../../utils/game-document/assets/task-contents';
import {
  TaskContentEditor,
  TaskContentEditorWindow
} from '../../../features/game-document/task-contents';
import cloneDeep from 'lodash.clonedeep';
import RequiredFields from '../../../types/required-fields';
import {
  ERR_DUPLICATE_NAME_VALUE,
  ERR_INPUT_REQUIRED
} from '../../../constants/text';
import { uuid } from '../../../types/common-helper';
import { TARMiniGame } from '../../../types/game-document/entities/task-content';
import { BodyType } from '../../../types/game-document/body-type';
import { AddNewOnDemandTaskCategoryAsync } from '../../../utils/game-document/assets/on-demand-task-categories';
import YesNoDialog from '../../../components/dialogs/yes-no-dialog';
import { useToggle } from 'usehooks-ts';
import { ARMiniGameContent } from '../../../constants/ar-mini-game';
import { useBreadcrumb } from '../../../contexts/breadcrumb';
import { GameDocument } from '../../../types/game-document';

const TaskContent = () => {
  const [state, setState] = useContext(GameDocumentContext);
  const navigate = useNavigate();
  const { gameId, zoneId, taskId, taskContentId } = useParams();
  const [showConfirmationDelete, setShowConfirmationDelete] = useToggle(false);
  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  const getEntityById = (entityId: string) =>
    state.gameDocument?.assets.taskContents?.find((i) => i.id === entityId)!;

  const [entity, setEntity] = useState<TaskContentEntity>(() =>
    getEntityById(taskContentId!)
  );
  const [requiredFields, setRequiredFields] = useState<
    RequiredFields<TaskContentEntity>[]
  >([{ name: 'name', errorMessage: '' }]);

  const addPreMessageResource = (taskContent: TaskContentEntity) => {
    const preMessageGuid = uuid();
    AddResourceAsync(
      state.gameDocument!,
      'PreMessage',
      '',
      'text-md',
      '',
      preMessageGuid
    ).then((response) => {
      setState((prev) => UpdateGameDocState(prev, response));
    });

    UpdateTaskContentAsync(
      state.gameDocument!,
      taskContentId!,
      {
        ...entity,
        preMessageResId: preMessageGuid
      },
      false
    ).then((response) => {
      setState((prev) => UpdateGameDocState(prev, response));
    });
  };

  useEffect(() => {
    let taskContent = state.gameDocument?.assets.taskContents?.find(
      (i) => i.id === taskContentId
    )!;
    /*
       if the task content does not yet have a preMessageResId, 
       it will be added to the task content and resources
    */
    if (taskContent && !taskContent.preMessageResId) {
      addPreMessageResource(taskContent);
    }
  }, []);

  useEffect(() => {
    setEntity(getEntityById(taskContentId!));
  }, [state]);

  useEffect(() => {
    // Set the page title.
    document.title = `${GetResourceValue(state.gameDocument!, entity.titleResId ?? '')} - ${state.gameDocument?.name}`;
  }, [state, entity]);

  const handleEntityChange = (entityKey: string, value: string) => {
    setRequiredFields((prev) =>
      prev.map((taskContent: RequiredFields<TaskContentEntity>) => {
        const copyTaskContent = cloneDeep({ ...taskContent });
        copyTaskContent.errorMessage = '';
        if (copyTaskContent.name === (entityKey as keyof TaskContentEntity)) {
          const duplicateValueIndex: number =
            state.gameDocument?.assets.taskContents
              ?.filter((item: TaskContentEntity) => item.id !== entity.id)
              .findIndex(
                (item: TaskContentEntity) =>
                  item.name.toLowerCase() === value.toLowerCase()
              ) ?? -1;
          if (value === '') {
            copyTaskContent.errorMessage = ERR_INPUT_REQUIRED;
          } else if (duplicateValueIndex !== -1) {
            copyTaskContent.errorMessage = ERR_DUPLICATE_NAME_VALUE;
          }
        }
        return copyTaskContent;
      })
    );

    const selectedAR = ARMiniGameContent[value as TARMiniGame];
    let miniGameEntity = {};

    if (selectedAR) {
      miniGameEntity = {
        ARMiniGame: value as TARMiniGame,
        name: value,
        description: selectedAR.description,
        bodyType: 'HTML' as BodyType,
        ARMiniGameSetting: {
          score: selectedAR.setting.score,
          ...((value as TARMiniGame) === 'Meerkats Moves' && {
            manualScoring: {
              min: 300,
              max: 500
            }
          }),
          ...(selectedAR.setting.timer !== 0 && {
            scoringReduction: {
              maximumTimeLimit: selectedAR.setting.timer,
              scoreReduction: undefined,
              scoreReductionInterval: undefined
            }
          }),
          ...(selectedAR.setting.mode !== undefined && {
            mode: selectedAR.setting.mode
          })
        }
      };
    }

    const result = {
      ...cloneDeep(entity),
      [entityKey]: value,
      ...(entityKey === 'ARMiniGame' &&
        value !== '' && {
          ...miniGameEntity
        })
    };

    if (entityKey === 'ARMiniGame' && value === '') {
      delete result.ARMiniGame;
      delete result.ARMiniGameSetting;
    }

    setEntity(result);

    UpdateTaskContentAsync(
      state.gameDocument!,
      taskContentId!,
      result,
      false
    ).then(async (response) => {
      let result = response;

      if (entityKey === 'onDemandTaskCategory' && value) {
        result = await AddNewOnDemandTaskCategoryAsync(result, value.trim());
      }

      setState((prev) => UpdateGameDocState(prev, result));
    });
  };

  const [entityEditorIsVisible, setEntityEditorIsVisible] =
    useState<boolean>(false);
  const toggleEntityEditor = () => {
    setEntityEditorIsVisible(!entityEditorIsVisible);
  };

  const handleEntityEditorSubmit = (
    editorEntity: EntityEditor<TaskContentEntity>,
    createTask?: boolean,
    title?: string
  ) => {
    const titleGuid = uuid(); //titleResId
    const preMessageGuid = uuid(); //preMessageResId
    const contentGuid = uuid(); //contentResId
    const taskContentId = uuid();

    const addTitleResource = AddResourceAsync(
      state.gameDocument!,
      'Title',
      '',
      'text',
      title ?? '',
      titleGuid
    );
    const addContentResource = AddResourceAsync(
      state.gameDocument!,
      'Content',
      '',
      'text-md',
      '',
      contentGuid
    );
    const addPreMessageResource = AddResourceAsync(
      state.gameDocument!,
      'PreMessage',
      '',
      'text-md',
      '',
      preMessageGuid
    );
    Promise.all([
      addTitleResource,
      addContentResource,
      addPreMessageResource
    ]).then(async () => {
      const updatedGameDocument = await AddTaskContentAsync(
        state.gameDocument!,
        editorEntity.entity.name,
        editorEntity.entity.description,
        editorEntity.entity.bodyType!,
        titleGuid,
        preMessageGuid,
        contentGuid,
        taskContentId
      );

      let finalGameDocument: GameDocument = updatedGameDocument;

      if (createTask) {
        // Create a Task from the Task Content metadata
        const taskTitleGuid = uuid();
        const defaultTaskAvailableIconResId =
          state.gameDocument?.settings.designer
            ?.defaultTaskAvailableIconResId || '';
        const defaultTaskCompletedIconResId =
          state.gameDocument?.settings.designer
            ?.defaultTaskCompletedIconResId || '';

        const taskEntity: TaskEntity = {
          id: '',
          name: editorEntity.entity.name,
          description: editorEntity.entity.description,
          titleResId: taskTitleGuid,
          imageResId: defaultTaskAvailableIconResId,
          completeImageResId: defaultTaskCompletedIconResId,
          taskContentId: taskContentId,
          hideWhenComplete:
            state.gameDocument && !state.gameDocument.settings.designer
              ? false
              : !Boolean(
                  state.gameDocument?.settings.designer
                    ?.taskCompletionVisibility
                )
        };

        const taskTitleResource: ResourceEntity = {
          id: taskTitleGuid,
          name: 'Title',
          description: '',
          type: 'text',
          value: title ?? ''
        };

        const taskEditorEntity: EntityEditor<TaskEntity> = {
          isNew: true,
          entity: taskEntity
        };

        const taskResourceEntities: EntityEditor<ResourceEntity>[] = [
          { isNew: true, entity: taskTitleResource }
        ];

        const docWithTask = await SaveEntityEditorAsync(
          finalGameDocument,
          taskEditorEntity,
          taskResourceEntities
        );

        if (docWithTask) {
          finalGameDocument = docWithTask as GameDocument;
        }
      }

      setState((state) => UpdateGameDocState(state, finalGameDocument));
      setEntityEditorIsVisible(false);
      navigate(
        `/designer/${state.gameId}/assets/task-content/${taskContentId}`
      );
    });
  };

  const onDeleteEntity = async (entityId: string) => {
    DeleteTaskContentAsync(state.gameDocument!, entityId).then(
      (updatedGameDocument) => {
        setState((state) => UpdateGameDocState(state, updatedGameDocument));
        navigate('../task-content');
      }
    );
  };

  const getTaskContentTitle = (taskContentId: string | undefined) => {
    if (taskContentId) {
      const taskContent = GetTaskContentById(
        state.gameDocument!,
        taskContentId
      );
      if (taskContent) {
        return GetResourceValue(
          state.gameDocument!,
          taskContent.titleResId ?? ''
        );
      }
    }
  };

  const getZoneTitle = (zoneId: string | undefined) => {
    if (zoneId) {
      const zone = GetZoneById(state.gameDocument, zoneId);
      if (zone) {
        const title = GetResourceValue(state.gameDocument!, zone.titleResId!);
        return title === '' ? zone.name : title;
      }
    }
  };

  const getTaskTitle = (taskId: string | undefined) => {
    if (taskId) {
      const task = GetTaskById(state.gameDocument!, taskId);
      if (task) {
        return task.name!;
      }
    }
  };

  useEffect(() => {
    if (!state.gameDocument || !taskContentId) return;
    const breadcrumbPaths = [
      { path: '/designer/:gameId/assets/*', text: 'Assets' },
      { path: '/designer/:gameId/assets/task-content/*', text: 'Task Content' },
      {
        path: '/designer/:gameId/assets/task-content/:taskContentId/*',
        text: getTaskContentTitle(taskContentId)
      },
      { path: '/designer/:gameId/assets/tasks/*', text: 'Tasks' },
      {
        path: '/designer/:gameId/assets/tasks/:taskId/*',
        text: getTaskTitle(taskId)
      },
      {
        path: '/designer/:gameId/assets/tasks/:taskId/:taskContentId/*',
        text: getTaskContentTitle(taskContentId)
      },

      { path: '/designer/:gameId/map/*', text: 'Map' },
      {
        path: '/designer/:gameId/map/:zoneId/*',
        text: getZoneTitle(zoneId)
      },
      {
        path: '/designer/:gameId/map/:zoneId/:taskContentId/*',
        text: getTaskContentTitle(taskContentId)
      }
    ];

    const newBreadcrumb = [];
    for (const config of breadcrumbPaths) {
      const match = matchPath(config.path, location.pathname);

      if (match && config.text) {
        newBreadcrumb.push({ text: config.text, to: match.pathnameBase });
      }
    }

    setBreadcrumb(newBreadcrumb);
    setEntity(getEntityById(taskContentId));

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname, state.gameDocument, taskContentId]);

  return (
    <>
      <Toolbar
        title={GetResourceValue(state.gameDocument!, entity.titleResId ?? '')}>
        {taskId && (
          <Link
            to={`/designer/${gameId}/map/task/${taskId}`}
            className={
              'ml-0 k-button k-button-md k-button-solid k-button-solid-base k-rounded-md me-1'
            }>
            Back
          </Link>
        )}
        <Button onClick={toggleEntityEditor} themeColor={'primary'}>
          Add task content
        </Button>
        <Button
          onClick={setShowConfirmationDelete}
          themeColor={'error'}
          fillMode={'flat'}>
          <span className="material-symbols-outlined">delete</span>
        </Button>

        {entityEditorIsVisible && (
          <TaskContentEditorWindow
            toggleDialog={toggleEntityEditor}
            onSubmit={handleEntityEditorSubmit}
            onClose={toggleEntityEditor}
            editorEntity={{
              isNew: true,
              entity: { id: '', name: '', description: '' }
            }}
            editorMode={'basic'}
          />
        )}
      </Toolbar>
      <FluidForm>
        <TaskContentEditor
          editorMode={'full'}
          entity={entity}
          handleEntityChange={handleEntityChange}
          requiredFields={requiredFields}
        />
      </FluidForm>

      {showConfirmationDelete && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={(action) =>
            action === 'yes'
              ? onDeleteEntity(entity.id)
              : setShowConfirmationDelete()
          }
          onClose={() => setShowConfirmationDelete()}>
          Are you sure you want to remove the task content?
        </YesNoDialog>
      )}
    </>
  );
};

export default TaskContent;
